import { z } from 'zod';
import { CharacterAttributesSchema, CharacterPlannedArcV2Schema } from './character-sub-entities-v2';

// =============================================================================
// UNIFIED IDEATION/EXTRACTION SCHEMA
// =============================================================================

/**
 * A unified schema for new character concepts, whether they are ideated by an
 * AI or extracted from text. Extends CharacterAttributesSchema with identity
 * fields (name, aliases, category).
 */
export const CharacterV2IdeaSchema = CharacterAttributesSchema.extend({
  name: z.string().describe("The character's proper name, without titles or honorifics (e.g., 'Sira' not 'Lady Sira')."),
  aliases: z.array(z.string()).optional().describe("Alternative names, nicknames, or titles for the character (e.g., 'Meg' for Margaret, 'The General')."),
  category: z.enum(['major', 'minor', 'extra']).describe("The character's importance in the story (major, minor, or extra)."),
}).describe("A unified concept for a new character, used for both AI-driven ideation and text extraction.");

/**
 * Extends the core idea schema with a temporary ID for use in the UI
 * before the character is saved to the database.
 */
export const IdeatedCharacterV2Schema = CharacterV2IdeaSchema.extend({
  tempId: z.string().describe("Temporary ID used during ideation, before the character is persisted to the database."),
  source: z.enum(['plot', 'ideation', 'extraction']).default('ideation').describe("Where this character idea originated from."),
  plannedCharacterArc: CharacterPlannedArcV2Schema.optional().describe(
    "The planned developmental arc for this character, generated after character ideation."
  ),
});

// Type inference
export type CharacterV2Idea = z.infer<typeof CharacterV2IdeaSchema>;
export type IdeatedCharacterV2 = z.infer<typeof IdeatedCharacterV2Schema>;
